import React, { useState, useMemo } from 'react';
import './NetHoursTable.css';
import { NetVarianceRow, computeNetHours } from './types';

interface NetHoursSummaryEntry {
  key: string;
  employee_name: string;
  role: string;
  net_hours: number;
  net_stat_hours: number;
  shifts: NetVarianceRow[];
}

type SortKey = 'employee_name' | 'role' | 'net_hours' | 'net_stat_hours';
interface SortState { key: SortKey; dir: 'asc' | 'desc' }

function buildSummary(rows: NetVarianceRow[]): NetHoursSummaryEntry[] {
  const map = new Map<string, NetHoursSummaryEntry>();
  for (const row of rows) {
    const key = `${row.employee_name}|${row.clocked_in_role}`;
    if (!map.has(key)) {
      map.set(key, { key, employee_name: row.employee_name, role: row.clocked_in_role, net_hours: 0, net_stat_hours: 0, shifts: [] });
    }
    const entry = map.get(key)!;
    const nh = computeNetHours(row);
    if (row.is_stat_holiday) { entry.net_stat_hours += nh; } else { entry.net_hours += nh; }
    entry.shifts.push(row);
  }
  return Array.from(map.values());
}

function compareEntries(a: NetHoursSummaryEntry, b: NetHoursSummaryEntry, key: SortKey): number {
  switch (key) {
    case 'employee_name': return a.employee_name.localeCompare(b.employee_name);
    case 'role':          return a.role.localeCompare(b.role);
    case 'net_hours':     return a.net_hours - b.net_hours;
    case 'net_stat_hours': return a.net_stat_hours - b.net_stat_hours;
    default:              return 0;
  }
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function SortIndicator({ sortKey, current, dir }: { sortKey: SortKey; current: SortKey; dir: 'asc' | 'desc' }) {
  if (sortKey !== current) return <span className="tc-nh-sort-idle">⇅</span>;
  return <span className="tc-nh-sort-active">{dir === 'asc' ? '▴' : '▾'}</span>;
}

interface NetHoursTableProps {
  rows: NetVarianceRow[];
  loading?: boolean;
  onRefresh: () => void;
}

export default function NetHoursTable({ rows, loading = false, onRefresh }: NetHoursTableProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState>({ key: 'employee_name', dir: 'asc' });

  const summary = useMemo(() => {
    const raw = buildSummary(rows);
    return [...raw].sort((a, b) => {
      const primary = compareEntries(a, b, sort.key);
      if (primary !== 0) return sort.dir === 'asc' ? primary : -primary;
      // Tie-break: employee name asc, then role asc
      return a.employee_name.localeCompare(b.employee_name) || a.role.localeCompare(b.role);
    });
  }, [rows, sort]);

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  const handleSort = (key: SortKey) => {
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const th = (key: SortKey, label: string, extraClass = '') => (
    <th
      className={`tc-nh-th tc-nh-th--sortable${extraClass ? ` ${extraClass}` : ''}`}
      onClick={() => handleSort(key)}
    >
      <span className="tc-nh-th-inner">
        {label}
        <SortIndicator sortKey={key} current={sort.key} dir={sort.dir} />
      </span>
    </th>
  );

  return (
    <div className="tc-nh-container">
      <div className="tc-topbar">
        <span className="tc-topbar-count">
          {summary.length} {summary.length === 1 ? 'entry' : 'entries'}
        </span>
        <button className="tc-refresh-btn" onClick={onRefresh} disabled={loading} title="Apply filters and reload">
          {loading ? '⟳ Loading…' : '↻ Refresh'}
        </button>
      </div>

      <div className="tc-nh-scroll">
        <table className="tc-nh-table">
          <thead>
            <tr className="tc-nh-thead-row">
              <th className="tc-nh-th tc-nh-th--expand" />
              {th('employee_name',  'Employee')}
              {th('role',           'Role')}
              {th('net_hours',      'Net Hours',      'tc-nh-th--num')}
              {th('net_stat_hours', 'Net Stat Hours', 'tc-nh-th--num')}
            </tr>
          </thead>
          <tbody>
            {summary.length === 0 ? (
              <tr><td colSpan={5} className="tc-nh-empty-cell" /></tr>
            ) : (
              summary.map(entry => {
                const isOpen = expanded.has(entry.key);
                // Drill-down shifts sorted by date asc
                const drillShifts = [...entry.shifts].sort((a, b) => a.date.localeCompare(b.date));
                return (
                  <React.Fragment key={entry.key}>
                    <tr className="tc-nh-row" onClick={() => toggleExpand(entry.key)}>
                      <td className="tc-nh-td tc-nh-td--expand">
                        <span className="tc-nh-chevron">{isOpen ? '▼' : '▶'}</span>
                      </td>
                      <td className="tc-nh-td">{entry.employee_name}</td>
                      <td className="tc-nh-td">{entry.role}</td>
                      <td className="tc-nh-td tc-nh-td--num">{entry.net_hours.toFixed(2)}</td>
                      <td className="tc-nh-td tc-nh-td--num">{entry.net_stat_hours.toFixed(2)}</td>
                    </tr>

                    {isOpen && (
                      <tr className="tc-nh-drilldown-row">
                        <td colSpan={5} className="tc-nh-drilldown-cell">
                          <table className="tc-nh-sub-table">
                            <thead>
                              <tr>
                                <th className="tc-nh-sub-th">Date</th>
                                <th className="tc-nh-sub-th">Start</th>
                                <th className="tc-nh-sub-th">End</th>
                                <th className="tc-nh-sub-th">Clock In</th>
                                <th className="tc-nh-sub-th">Clock Out</th>
                                <th className="tc-nh-sub-th tc-nh-sub-th--num">Hrs Sched</th>
                                <th className="tc-nh-sub-th tc-nh-sub-th--num">Hrs Worked</th>
                                <th className="tc-nh-sub-th tc-nh-sub-th--num">Net Hrs</th>
                              </tr>
                            </thead>
                            <tbody>
                              {drillShifts.map(shift => (
                                <tr key={shift.id} className="tc-nh-sub-row">
                                  <td className="tc-nh-sub-td">{fmtDate(shift.date)}</td>
                                  <td className="tc-nh-sub-td">{fmtTime(shift.start)}</td>
                                  <td className="tc-nh-sub-td">{fmtTime(shift.end)}</td>
                                  <td className="tc-nh-sub-td">{fmtTime(shift.clock_in)}</td>
                                  <td className="tc-nh-sub-td">{fmtTime(shift.clock_out)}</td>
                                  <td className="tc-nh-sub-td tc-nh-sub-td--num">{shift.hours_scheduled.toFixed(2)}</td>
                                  <td className="tc-nh-sub-td tc-nh-sub-td--num">{shift.hours_worked.toFixed(2)}</td>
                                  <td className="tc-nh-sub-td tc-nh-sub-td--num">{computeNetHours(shift).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
